/* eslint-disable no-alert */
import { Currency } from "@daytrip/legacy-enums";
import type { HostAgency, HyperwalletInformation } from "@daytrip/legacy-models";
import type { SimpleCountry } from "@daytrip/legacy-models";
import type { AutomaticEmailAttempt } from "@daytrip/legacy-models";
import { SimpleUser } from "@daytrip/legacy-models";
import { Chargeback } from "@daytrip/legacy-models";
import { MangopayInformation } from "@daytrip/legacy-models";
import { Payment } from "@daytrip/legacy-models";
import { PaymentRequest } from "@daytrip/legacy-models";
import { ReferralCode } from "@daytrip/legacy-models";
import { TravelAgentChangeLog } from "@daytrip/legacy-models";
import { transformValidationErrorsToArrayString } from "@daytrip/legacy-transformers";
import { OrderDataForOrdersPage } from "@daytrip/legacy-utils";
import { evaluateTravelAgentCategory } from "@daytrip/legacy-utils";
import { RetrieveOrdersOptions } from "@daytrip/legacy-options";
import type { RetrieveChargebacksOptions, RetrievePaymentRequestsOptions } from "@daytrip/legacy-options";
import { isUndefinedOrNull, isNotUndefinedOrNull } from "@daytrip/utils";
import autobind from "autobind-decorator";
import { classToPlain, plainToClass } from "class-transformer";
import type { ValidationError } from "class-validator";
import { validate } from "class-validator";
import { action, computed, extendObservable, observable, toJS } from "mobx";

import { globalManagementLogger } from "../../global-logger";
import { ChargebackOperator } from "../../operators/ChargebackOperator";
import { PaymentOperator } from "../../operators/PaymentOperator";
import { PaymentRequestOperator } from "../../operators/PaymentRequestOperator";
import { PageStore } from "../../stores/PageStore";
import { PaginatedDataStore } from "../../stores/PaginatedDataStore";
import {
    getPhoneNumberExtendedValidationMessage,
    validatePhoneNumberWithAlert,
} from "../../utils/validateUserPhoneNumber";
import { apiTrpcClient } from "@/trpc/api";
import { TravelAgentModel, TravelAgentUserModel } from "@daytrip/api";
import { UserProfile } from "@daytrip/api";

import type { TravelAgentPageRouter } from "./TravelAgentPageRouter";
@autobind
export class TravelAgentPageStore extends PageStore<TravelAgentPageRouter, {}> {
    @observable
    public automaticEmailAttempts: Array<AutomaticEmailAttempt> = [];

    @observable
    public user: UserProfile | undefined;

    @observable
    public editedUser: UserProfile | undefined;

    @observable
    public countries: Array<SimpleCountry> | undefined;

    @observable
    public hostAgencies: Array<HostAgency> = [];

    @observable
    public orders?: PaginatedDataStore<RetrieveOrdersOptions, OrderDataForOrdersPage>;

    @observable
    public apiPartnerOrders?: PaginatedDataStore<RetrieveOrdersOptions, OrderDataForOrdersPage>;

    @observable
    public payments?: Array<Payment>;

    @observable
    public paymentOperators: Array<PaymentOperator> = [];

    @observable
    public paymentRequestOperators: Array<PaymentRequestOperator> = [];

    @observable
    public paymentRequestOperator?: PaymentRequestOperator;

    @observable
    public chargebackOperators: Array<ChargebackOperator> = [];

    @observable
    public editedUserValidationErrors: Array<ValidationError> = [];

    @observable
    public existingUser?: UserProfile;

    @observable
    public isPaymentRequestFormVisible: boolean = false;

    @observable
    public referralCode?: ReferralCode;

    @observable
    public users?: Array<SimpleUser>;

    @action
    public async onFetchData() {
        await this.fetchContent();
    }

    @action
    public async fetchContent() {
        const { userId } = this.pageRouter;

        this.user = undefined;
        this.editedUser = undefined;

        if (this.user && (this.user as TravelAgentUserModel)._id !== userId) {
            this.orders = undefined;
            this.apiPartnerOrders = undefined;
        }

        if (userId) {
            const user = await apiTrpcClient.travelAgent.retrieveTravelAgent.query({
                travelAgentId: userId,
            });
            user.mangopayInformation = plainToClass(MangopayInformation, user.mangopayInformation);
            this.user = user;

            if (!this.user.travelAgent) {
                this.userEdit();
            }
        } else {
            this.user = classToPlain(new TravelAgentUserModel()) as UserProfile;
            this.userEdit();
        }

        if (userId) {
            await this.fetchOrders(userId);

            if (this.user?.travelAgent?.ownerId) {
                this.fetchTravelAgentOwner(this.user.travelAgent.ownerId);
            }

            this.fetchPaymentInformation();
        }

        this.fetchCountries();
        this.fetchHostAgencies();

        if (userId) {
            this.fetchReferralCode(userId);
        }
    }

    @action
    public fetchOrders(userId: string) {
        if (!this.orders) {
            this.orders = new PaginatedDataStore(
                OrderDataForOrdersPage,
                RetrieveOrdersOptions,
                this.rpcClient.order.retrieveOrdersDataForOrdersPage,
                this.rpcClient.order.retrieveOrdersCount,
                this.pageRouter,
                "orders_",
                {
                    skip: 1,
                    limit: 30,
                    userIds: [userId],
                    sortBy: "departureAt",
                    sortDirection: -1,
                },
            );
        } else {
            this.orders.fetch();
        }
    }

    @action
    public fetchApiPartnerOrders(userId: string) {
        if (!this.apiPartnerOrders) {
            this.apiPartnerOrders = new PaginatedDataStore(
                OrderDataForOrdersPage,
                RetrieveOrdersOptions,
                this.rpcClient.order.retrieveOrdersDataForOrdersPage,
                this.rpcClient.order.retrieveOrdersCount,
                this.pageRouter,
                "affiliateOrders_",
                {
                    skip: 1,
                    limit: 30,
                    apiPartnerIds: [userId],
                    sortBy: "departureAt",
                    sortDirection: -1,
                },
            );
        } else {
            this.apiPartnerOrders.fetch();
        }
    }

    @action
    public async fetchTravelAgentOwner(ownerId: string) {
        const [travelAgentOwner] = await apiTrpcClient.user.retrieveSimpleUsers.query({ options: { userIds: [ownerId] } });

        if (travelAgentOwner) {
            this.travelAgentOwner = travelAgentOwner;
            this.users = [this.travelAgentOwner];
        } else {
            this.travelAgentOwner = new SimpleUser();
            this.travelAgentOwner._id = this.user?.travelAgent?.ownerId || "";
            this.travelAgentOwner.fullName = "Unknown";
        }
    }

    @action
    public async fetchCountries() {
        const countries = await this.rpcClient.content.retrieveSimpleCountries({});
        this.countries = countries.sort((a, b) => a.englishName.localeCompare(b.englishName));
    }

    @action
    public async fetchHostAgencies() {
        const hostAgenciesData = await this.rpcClient.hostAgency.retrieveHostAgencies();
        this.hostAgencies = hostAgenciesData;
    }

    @action
    public async fetchReferralCode(userId: string) {
        if (!userId) {
            this.referralCode = undefined;
            return;
        }

        try {
            this.referralCode = await this.rpcClient.referral.retrieveReferralCodeByUserId(userId);
        } catch (e: any) {
            this.referralCode = undefined;
        }
    }

    @action
    public async fetchPaymentInformation(): Promise<void> {
        if (!this.user) {
            return;
        }

        const { validationInfo, payments: paymentEntities } =
            await this.rpcClient.payment.retrievePaymentsWithValidationInfo({
                userIds: [this.user._id],
            });
        const payments = plainToClass(Payment, paymentEntities);

        let chargebackOperators: Array<ChargebackOperator> = [];

        if (this.orders && this.orders.data && this.orders.data.length > 0) {
            const chargebacksOptions: RetrieveChargebacksOptions = {
                orderIds: this.orders.data.map((o) => o.simpleOrder.orderId),
                includeNotConnected: true,
            };

            const chargebacks = plainToClass(
                Chargeback,
                await this.rpcClient.payment.retrieveChargebacks(chargebacksOptions),
            );

            if (chargebacks) {
                chargebackOperators = chargebacks.map(
                    (cb) =>
                        new ChargebackOperator({
                            modelConstructor: Chargeback,
                            model: cb,
                            modules: null,
                            data: null,
                        }),
                );
            }
        }

        const paymentRequestsOptions = {} as RetrievePaymentRequestsOptions;
        paymentRequestsOptions.userIds = [this.user._id];
        const paymentRequests = plainToClass(
            PaymentRequest,
            await this.rpcClient.payment.retrievePaymentRequests(paymentRequestsOptions),
        );
        if (paymentRequests) {
            this.paymentRequestOperators = [];
            this.paymentOperators = [];
            paymentRequests.forEach(async (pr) => {
                // trying to find fulfilled payment
                let payment = payments.find((p) => p.paymentRequestId === pr._id && !!p.fulfilledAt);
                if (!payment) {
                    // if no fulfilled payment found - take any
                    payment = payments.find((p) => p.paymentRequestId === pr._id);
                }

                if (!payment) {
                    this.paymentRequestOperators.push(
                        new PaymentRequestOperator({
                            modelConstructor: PaymentRequest,
                            model: pr,
                            modules: null,
                            data: null,
                        }),
                    );
                } else {
                    let isChargebackEnabled = false;

                    try {
                        isChargebackEnabled = !!validationInfo?.[payment._id];
                    } catch (e: any) {
                        globalManagementLogger.error(e);
                    }

                    this.paymentOperators.push(
                        new PaymentOperator({
                            modelConstructor: Payment,
                            model: payment,
                            paymentRequest: pr,
                            modules: null,
                            data: {
                                isChargebackEnabled,
                            },
                            chargebacks: chargebackOperators.filter(
                                (ch) => ch.m.paymentId === pr._id || (payment && ch.m.paymentId === payment._id),
                            ),
                            onUpdate: this.fetchPaymentInformation,
                        }),
                    );
                }
            });

            const paymentOperatorsWithoutPaymentRequest: Array<PaymentOperator> = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const payment of payments.filter((p) => !p.paymentRequestId)) {
                let isChargebackEnabled = false;

                try {
                    isChargebackEnabled = !!validationInfo?.[payment._id];
                } catch (e: any) {
                    globalManagementLogger.error(e);
                }

                const po = new PaymentOperator({
                    modelConstructor: Payment,
                    model: payment,
                    modules: null,
                    data: {
                        isChargebackEnabled,
                    },
                    chargebacks: chargebackOperators.filter((ch) => ch.m.paymentId === payment._id),
                    onUpdate: this.fetchPaymentInformation,
                });

                paymentOperatorsWithoutPaymentRequest.push(po);
            }

            this.paymentOperators = this.paymentOperators.concat(paymentOperatorsWithoutPaymentRequest);
        }

        const paymentRequest = new PaymentRequest();
        paymentRequest.userId = this.user._id;

        const paymentRequestOperator = new PaymentRequestOperator({
            modelConstructor: PaymentRequest,
            model: paymentRequest,
            modules: null,
            data: null,
            validateOptions: { skipMissingProperties: true },
            onSave: async (model: PaymentRequest) => {
                await this.rpcClient.payment.createPaymentRequest(toJS(model));

                this.fetchPaymentInformation();

                this.paymentRequestOperator!.edit((pr) => {
                    pr.description = "";
                    pr.amount = 0;
                });
            },
        });

        this.paymentRequestOperator = paymentRequestOperator;
        this.chargebackOperators = chargebackOperators.filter((cho) => !cho.model.paymentId);
    }

    @action
    public userUpdateTravelAgentDiscountCoefficient(value: number) {
        const travelAgent = (this.editedUser as TravelAgentUserModel).travelAgent;
        travelAgent.discountCoefficient = value;
        travelAgent.category = evaluateTravelAgentCategory(travelAgent.commissionPercentage, this.selectedHostAgency);
    }

    @action
    public userUpdateTravelAgentCommission(value: number) {
        const travelAgent = (this.editedUser as TravelAgentUserModel).travelAgent;
        travelAgent.commissionPercentage = value;
        travelAgent.category = evaluateTravelAgentCategory(travelAgent.commissionPercentage, this.selectedHostAgency);
    }

    @action
    public userUpdateTravelAgentCurrency(value: Currency) {
        (this.editedUser as TravelAgentUserModel).travelAgent.currency = value;
    }

    @action
    public userUpdateTravelAgentId(value: string) {
        (this.editedUser as TravelAgentUserModel).travelAgent.agentId = value;
    }

    @action
    public userUpdateHyperwalletInformationToken(value: string) {
        ((this.editedUser as TravelAgentUserModel).hyperwalletInformation as HyperwalletInformation).token = value;
    }

    @action
    public userUpdateFirstName(value: string) {
        (this.editedUser as TravelAgentUserModel).firstName = value;
    }

    @computed
    public get firstNameValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "firstName");
        if (!validationErrors) {
            return undefined;
        }
        return JSON.stringify(validationErrors.constraints);
    }

    @action
    public userUpdateLastName(value: string) {
        (this.editedUser as TravelAgentUserModel).lastName = value;
    }

    @computed
    public get lastNameValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "lastName");
        if (!validationErrors) {
            return undefined;
        }
        return JSON.stringify(validationErrors.constraints);
    }

    @action
    public userUpdateEmail(value: string) {
        (this.editedUser as TravelAgentUserModel).email = value.toLowerCase();
        this.existingUser = undefined;
    }

    @computed
    public get emailValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "email");
        if (!validationErrors) {
            return undefined;
        }
        return JSON.stringify(validationErrors.constraints);
    }

    @action
    public userUpdateOtherEmail(index: number, value: string) {
        (this.editedUser as TravelAgentUserModel).otherEmails[index] = value;
    }

    @computed
    public get otherEmailsValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "otherEmails");
        if (!validationErrors) {
            return undefined;
        }
        return JSON.stringify(validationErrors.constraints);
    }

    @action
    public userRemoveOtherEmail(index: number) {
        (this.editedUser as TravelAgentUserModel).otherEmails.splice(index, 1);
    }

    @action
    public userAddOtherEmail(value: string) {
        (this.editedUser as TravelAgentUserModel).otherEmails.push(value);
    }

    @action
    public userUpdatePaypalEmail(value: string) {
        (this.editedUser as TravelAgentUserModel).paypalEmail = value.toLowerCase();
        this.existingUser = undefined;
    }

    @computed
    public get paypalEmailValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "paypalEmail");
        if (!validationErrors) {
            return undefined;
        }
        return JSON.stringify(validationErrors.constraints);
    }

    @action
    public userUpdatePhoneNumber(value: string) {
        (this.editedUser as TravelAgentUserModel).phoneNumber = value;
    }

    @computed
    public get phoneNumberValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "phoneNumber");
        const validationMessages = validationErrors?.constraints
            ? JSON.stringify(validationErrors.constraints)
            : undefined;

        return getPhoneNumberExtendedValidationMessage(
            ((this.editedUser ?? this.user) as TravelAgentUserModel).phoneNumber,
            validationMessages,
            Boolean(this.user?.travelAgent),
        );
    }

    @computed
    public get selectedHostAgency(): HostAgency | undefined {
        if (this.hostAgencies && this.editedUser?.travelAgent?.hostAgencyId) {
            return this.hostAgencies.find(
                (hostAgency) => hostAgency._id === this.editedUser?.travelAgent?.hostAgencyId,
            );
        }
    }

    @action
    public userUpdateCustomerNote(value: string) {
        this.editedUser!.customerNote = value;
    }

    @action
    public userUpdateWhatsappNumber(value: string) {
        this.editedUser!.whatsappNumber = value;
    }

    @action
    public travelAgentUpdateHostAgency(value: string) {
        const editedUser = this.editedUser?.travelAgent;

        if (!editedUser) {
            return;
        }

        if (isUndefinedOrNull(value)) {
            editedUser.hostAgencyId = value;
            editedUser.isHostAgencyAdmin = false;
            editedUser.category = evaluateTravelAgentCategory(editedUser.commissionPercentage, undefined);
            return;
        }

        if (this.hostAgencies) {
            const selectedHostAgency = this.hostAgencies.find((ha) => ha._id === value);
            if (selectedHostAgency) {
                editedUser.ownerId = selectedHostAgency.ownerId;
                editedUser.discountCoefficient = selectedHostAgency.discountCoefficient;
                editedUser.commissionPercentage = selectedHostAgency.commissionPercentage;
                editedUser.partnerBookingAvailable = selectedHostAgency.partnerBookingAvailable;
                editedUser.agentId = selectedHostAgency._id;
                editedUser.hostAgencyId = value;
                editedUser.category = evaluateTravelAgentCategory(editedUser.commissionPercentage, selectedHostAgency);
                editedUser.currency = selectedHostAgency.currency;
            }
        }
    }

    @computed
    public get whatsappNumberValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "whatsupNumber");
        if (!validationErrors) {
            return undefined;
        }
        return JSON.stringify(validationErrors.constraints);
    }

    @action
    public userUpdateOtherPhoneNumber(index: number, value: string) {
        (this.editedUser as TravelAgentUserModel).otherPhoneNumbers[index] = value;
    }

    @computed
    public get otherPhoneNumbersValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "otherPhoneNumbers");
        if (!validationErrors) {
            return undefined;
        }
        return JSON.stringify(validationErrors.constraints);
    }

    @action
    public userRemoveOtherPhoneNumber(index: number) {
        (this.editedUser as TravelAgentUserModel).otherPhoneNumbers.splice(index, 1);
    }

    @action
    public userAddOtherPhoneNumber(value: string) {
        (this.editedUser as TravelAgentUserModel).otherPhoneNumbers.push(value);
    }

    @action
    public userUpdateCountryId(value: string) {
        (this.editedUser as TravelAgentUserModel).countryId = value;
    }

    @computed
    public get countryIdValidationMessage(): string | undefined {
        const validationErrors = this.editedUserValidationErrors.find((ve) => ve.property === "countryId");
        if (!validationErrors) {
            return undefined;
        }
        return JSON.stringify(validationErrors.constraints);
    }

    @action
    public userEdit() {
        this.editedUser = toJS(this.user);

        if (!this.user?.travelAgent) {
            extendObservable(this.editedUser as TravelAgentUserModel, {
                travelAgent: classToPlain(new TravelAgentModel()),
            });
        }
    }

    @action
    public userEditCancelOrGoBack() {
        if (!this.pageRouter.userId) {
            window.history.back();
        } else {
            this.editedUser = undefined;
        }
    }

    @action
    public async userValidate() {
        const errorBothDiscountAndCommission =
            (this.editedUser?.travelAgent?.discountCoefficient ?? 0) > 0 &&
            (this.editedUser?.travelAgent?.commissionPercentage ?? 0) > 0 &&
            this.editedUser?.travelAgent?.hostAgencyId !== "INTRAIL";

        if (errorBothDiscountAndCommission) {
            alert("Only INTRAIL can have both discount and commission");
            return false;
        }

        const newUser = plainToClass(
            TravelAgentUserModel,
            toJS({
                ...this.editedUser,
                travelAgent: {
                    ...this.editedUser?.travelAgent!,
                    // This props are just populated to pass the validation, they are not used in the create/update mutation.
                    email: this.editedUser?.email!,
                    firstName: this.editedUser?.firstName!,
                    lastName: this.editedUser?.lastName!,
                },
            }),
        );
        this.editedUserValidationErrors = await validate(newUser, {
            skipMissingProperties: true,
            forbidUnknownValues: false,
        });

        return true;
    }

    @action
    public async userEditSave() {
        if (!this.editedUser) {
            return;
        }

        if (!(await this.userValidate())) {
            return;
        }

        if (this.editedUserValidationErrors.length > 0) {
            globalManagementLogger.info({ payload: toJS(this.editedUserValidationErrors) });

            const validationMessages = transformValidationErrorsToArrayString(this.editedUserValidationErrors).join(
                "\n",
            );
            alert(`Oh, something is wrong. :(\n\nValidation errors:\n${validationMessages}`);
            return;
        }

        if (
            !validatePhoneNumberWithAlert(
                ((this.editedUser ?? this.user) as TravelAgentUserModel).phoneNumber,
                Boolean(this.user?.travelAgent),
            )
        ) {
            return;
        }

        if (this.user && this.editedUser) {
            try {
                await apiTrpcClient.travelAgent.update.mutate({
                    userId: this.editedUser._id,
                    updateData: { ...this.editedUser, travelAgent: this.editedUser.travelAgent! },
                });
            } catch (e: any) {
                globalManagementLogger.error({ error: e });
                alert("Something went wrong during TA update: " + e);
                return;
            }

            if (isNotUndefinedOrNull(this.editedUser.version)) {
                this.editedUser.version += 1;
            } else {
                this.editedUser.version = 0;
            }

            if (
                this.editedUser.travelAgent &&
                (!this.user.travelAgent ||
                    (this.user.travelAgent && this.user.travelAgent.ownerId !== this.editedUser.travelAgent.ownerId))
            ) {
                this.travelAgentOwner = (
                    await apiTrpcClient.user.retrieveSimpleUsers.query({
                        options: { userIds: [this.editedUser.travelAgent.ownerId as string] },
                    })
                )[0] as SimpleUser;
                this.users = [this.travelAgentOwner];
            }
            this.user = toJS(this.editedUser);
            this.editedUser = undefined;
        } else {
            this.editedUser._id = await apiTrpcClient.travelAgent.create.mutate({
                ...this.editedUser,
                email: this.editedUser.email!,
                travelAgent: this.editedUser.travelAgent!,
            });
            this.pageRouter.openCreatedUser(this.editedUser._id);
        }
    }

    public async deleteUser() {
        try {
            await apiTrpcClient.user.deleteUser.mutate({ userId: (this.user as TravelAgentUserModel)._id });
            this.pageRouter.openUsersAfterDeletion();
        } catch (e: any) {
            alert("Something went wrong during deletion.");
            globalManagementLogger.error(e);
        }
    }

    public async checkIsPossibleUserDuplicate(): Promise<boolean> {
        const isEmailUpdated = this.user?.email !== this.editedUser?.email;

        if (this.editedUser && this.editedUser.email && isEmailUpdated) {
            const users = await apiTrpcClient.user.retrieveUsers.query({ options: { email: this.editedUser.email } });

            const [existingUser] = users;
            this.existingUser = plainToClass(TravelAgentUserModel, existingUser) as UserProfile;

            return !!existingUser;
        }
        return false;
    }

    @action
    public async userCreate() {
        if (!this.editedUser) {
            return;
        }

        const isUserAlreadyExist = await this.checkIsPossibleUserDuplicate();
        if (isUserAlreadyExist) {
            return;
        }

        if (!(await this.userValidate())) {
            return;
        }

        if (this.editedUserValidationErrors.length > 0) {
            globalManagementLogger.info({ payload: toJS(this.editedUserValidationErrors) });

            const validationMessages = transformValidationErrorsToArrayString(this.editedUserValidationErrors).join(
                "\n",
            );
            alert(`Oh, something is wrong. :(\n\nValidation errors:\n${validationMessages}`);
            return;
        }

        if (!validatePhoneNumberWithAlert(this.editedUser.phoneNumber, Boolean(this.user?.travelAgent))) {
            return;
        }

        const userId = await apiTrpcClient.travelAgent.create.mutate({
            ...this.editedUser,
            email: this.editedUser.email!,
            travelAgent: this.editedUser.travelAgent!,
        });
        this.user = this.editedUser;
        this.editedUser = undefined;
        this.pageRouter.openCreatedUser(userId);
    }

    public goToExistingUser() {
        if (this.existingUser) {
            this.pageRouter.goToExistingUser(this.existingUser);
        }
    }

    @observable
    public isReferralSending = false;

    @action
    public async sendInitialReferral() {
        this.isReferralSending = true;

        await this.rpcClient.referral.sendReferralInitialEmail((this.user as TravelAgentUserModel)._id);
        this.referralCode = await this.rpcClient.referral.retrieveReferralCodeByUserId(
            (this.user as TravelAgentUserModel)._id,
        );

        this.isReferralSending = false;
    }

    @action
    public setTravelAgent() {
        this.userEdit();
        extendObservable(this.editedUser as TravelAgentUserModel, {
            travelAgent: classToPlain(new TravelAgentModel()) as TravelAgentModel,
        });
    }

    public goToCreateOrder() {
        if (this.user) {
            this.pageRouter.openCreateOrder(this.user._id);
        }
    }

    @action
    public revealPaymentRequestCreation(): void {
        this.isPaymentRequestFormVisible = true;
    }

    @action
    public hidePaymentRequestCreation(): void {
        this.isPaymentRequestFormVisible = false;
    }

    @observable
    public travelAgentOwner?: SimpleUser;

    @action
    public setTravelAgentOwner(value?: string): void {
        if (this.editedUser && this.editedUser.travelAgent) {
            this.editedUser.travelAgent.ownerId = value;
        }
    }

    @action
    public setPartnerBookingAvailableForAgent(value: boolean): void {
        if (this.editedUser && this.editedUser.travelAgent) {
            this.editedUser.travelAgent.partnerBookingAvailable = value;
        }
    }

    @action
    public setIsHostAgencyAdmin(value?: boolean): void {
        if (this.editedUser && this.editedUser.travelAgent) {
            this.editedUser.travelAgent.isHostAgencyAdmin = value || false;
        }
    }

    @action
    public async onTravelAgentOwnerInputChange(value?: string): Promise<void> {
        if (value && value.length > 2) {
            if (!this.users) {
                this.users = [];
            }

            this.users = await apiTrpcClient.user.retrieveSimpleUsers.query({
                options: {
                    searchString: value,
                    isDriver: false,
                    isCompanyDriver: false,
                    isDriversCompany: false,
                }
            });
        }
    }

    public isDataFetched(): this is TravelAgentPageStore & TravelAgentPageStoreDataFetched {
        return !!(this.user || this.editedUser);
    }

    @observable
    public isPRPaymentProviderVisible: boolean = false;

    @action
    public setPRPaymentProviderVisible() {
        this.isPRPaymentProviderVisible = !this.isPRPaymentProviderVisible;
    }

    @observable
    public changeLogs?: TravelAgentChangeLog;

    @observable
    public changedByUsers?: Array<SimpleUser>;

    @observable
    public noChangesFound?: boolean;

    @action
    public async loadChangeLogs(): Promise<void> {
        if (!this.user) {
            this.noChangesFound = true;
            return;
        }

        this.changeLogs = plainToClass(
            TravelAgentChangeLog,
            await apiTrpcClient.travelAgent.getTravelAgentChangeLogs.query({ travelAgentId: this.user._id }),
        );

        if (isNotUndefinedOrNull(this.changeLogs)) {
            await this.loadChangedByUsers(
                this.changeLogs.logs.filter((l) => l.changedByUserId).map((l) => l.changedByUserId) as Array<string>,
            );
        } else {
            this.noChangesFound = true;
        }
    }

    @action
    public async loadChangedByUsers(userIds: Array<string>): Promise<void> {
        this.changedByUsers = await apiTrpcClient.user.retrieveSimpleUsers.query({ options: { userIds } });
    }
}

export interface TravelAgentPageStoreDataFetched {
    user: TravelAgentUserModel;
}
